import type { FlowStatus } from "./advisoryApi";
import { stepLabel } from "./format";
import type { NavGroup, NavItem } from "./nav";
import { PercentBar, checkClass, checkGlyph } from "./ui";

/**
 * Left column: QP's pages in the advisor's fixed order (nav.ts) as check-rows,
 * with the overall % bar on top. Not-applicable steps stay in place and say
 * what opens them. Every item is clickable (QP navigates by tabs, not a locked
 * stepper).
 */
export function StepNav({
  groups,
  status,
  selectedId,
  attempted,
  onSelect,
  submitted,
}: {
  submitted: boolean;
  groups: NavGroup[];
  status: FlowStatus | null;
  selectedId: string | null;
  attempted: boolean;
  onSelect: (item: NavItem) => void;
}) {
  const filled = status?.filledRequired;
  const total = status?.totalRequired;
  return (
    <nav className="adv-nav" aria-label="Advice steps">
      <div className="adv-nav-progress">
        <PercentBar value={status?.percent ?? null} />
        {typeof filled === "number" && typeof total === "number" && (
          <span className="muted">
            {filled} of {total} required items
          </span>
        )}
      </div>
      {groups.map((g) => (
        <div key={g.key} className="adv-nav-group">
          {g.title && <div className="adv-nav-group-title">{g.title}</div>}
          <ul className="check-list adv-nav-list">
            {g.items.map((it) => {
              const cls =
                it.status === "optional"
                  ? "check-row adv-check-optional"
                  : it.kind === "review"
                    ? status?.canSubmit || submitted
                      ? "check-row check-pass"
                      : "check-row adv-check-open"
                    : checkClass(it.status, attempted);
              const glyph =
                it.status === "optional" ? "+" : it.kind === "review" ? (status?.canSubmit || submitted ? "✓" : "") : checkGlyph(it.status, attempted);
              const hint =
                it.kind === "page"
                  ? it.steps.length === 1
                    ? stepLabel(it.steps[0]!)
                    : `${it.steps.filter((s) => s.status === "complete").length} of ${it.steps.filter((s) => s.status !== "not_applicable").length} complete`
                  : it.kind === "review"
                    ? submitted
                      ? "Submitted"
                      : status?.canSubmit
                        ? "Ready to submit"
                      : "Review and submit"
                    : "Optional";
              return (
                <li
                  key={it.id}
                  className={`${cls} adv-nav-item${selectedId === it.id ? " selected" : ""}`}
                  aria-current={selectedId === it.id ? "step" : undefined}
                >
                  <button type="button" className="adv-nav-btn" onClick={() => onSelect(it)}>
                    <span className="check-icon">{glyph}</span>
                    <span className="adv-nav-text">
                      <strong>{it.title}</strong>
                      <span className={it.status === "not_applicable" ? "adv-nav-hint adv-nav-hint-wrap" : "adv-nav-hint"}>
                        {it.subtitle && it.kind === "page" ? `${it.subtitle} · ` : ""}
                        {it.status === "not_applicable" ? (it.naHint ?? "Not applicable yet") : hint}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </nav>
  );
}
